import React from 'react';
import { connect } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import classNames from 'classnames';
import { shopDetailProductsRequest } from 'actions/onlineStore';
import loadingIcon from 'assets/images/backoffice/online-store/loading.svg';
import TooltipHint from 'components/TooltipHint/TooltipHint';
import styles from './ShopDetailProductTable.css';

const attrCompletionTooltip = (
  <span className={styles['tooltip-text']}>
    Go to{' '}
    <a href="/library/list" target="_blank">
      item library
    </a>{' '}
    page. When you add or edit an item, make sure all "Mandatory for Online
    Store" label is complete
  </span>
);

const channelSpecificText = {
  facebook: {
    text: 'Facebook and Instagram Shop.',
  },
  google: {
    text: 'Google.',
  },
};

class ShopDetailProductTable extends React.Component {
  render() {
    const { shopDetail } = this.props.shop;
    if (!shopDetail) {
      return null;
    }

    const channel = shopDetail.channel.toLowerCase();

    return (
      <div>
        <div
          className={classNames(styles.hint, styles['mb-20'], styles['mt-20'])}
        >
          <div className={classNames(styles['mb-5'])}>
            <i className={classNames('glyphicon glyphicon-alert')} />
            <span className={classNames(styles['ml-10'])}>
              <strong> Requirement for uploading product</strong>
            </span>
          </div>
          <div>
            Only Products with <strong>complete Online Store Attributes</strong>
            <span className={classNames(styles.tooltip)}>
              <TooltipHint
                color="#2D7AB8"
                tooltip={attrCompletionTooltip}
                placement="bottom"
              />
            </span>
            will be uploaded to {channelSpecificText[channel].text}
          </div>
        </div>
        <div className={`${styles['table-wrapper']}`}>
          <InfiniteScroll
            pageStart={1}
            loadMore={this._onReachBottom}
            hasMore={this._hasMore()}
            useWindow={false}
            threshold={10}
            initialLoad={false}
          >
            <table
              className={`${
                styles['table-content']
              } moka-table table-truncate table-responsive mk-table-fixed mk-table-clickable table table-striped`}
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Upload Status</th>
                  <th>Info</th>
                </tr>
              </thead>
              <tbody>{this._renderRows()}</tbody>
            </table>
          </InfiniteScroll>
        </div>
      </div>
    );
  }

  _renderRows() {
    const { shopDetailProducts, shopDetail } = this.props.shop;
    if (shopDetailProducts.data.length === 0) {
      return null;
    }

    const isUploading = shopDetail.item_sync_status === 'IN_PROGRESS';

    return shopDetailProducts.data.map(item => {
      const variants = item.variants.map(variant => {
        return (
          <tr>
            <td>
              <div className={classNames(styles['img-item-outer'])} />
              {variant.variant_name}
            </td>
            <td>{this._renderStatus(isUploading, variant)}</td>
            <td>{this._renderInfo(isUploading, variant)}</td>
          </tr>
        );
      });
      return [
        <tr>
          <td>
            <div className={classNames(styles['img-item-outer'])}>
              <img
                src={item.image_url}
                className={classNames(styles['img-item'])}
              />
            </div>
            <strong>{item.name}</strong>
          </td>
        </tr>,
        variants,
      ];
    });
  }

  _renderInfo = (isUploading, itemVariant) => {
    if (isUploading || !['FAILED', 'PENDING'].includes(itemVariant.status)) {
      return null;
    }

    switch (itemVariant.error_type) {
      case 'MISSING_ATTRIBUTES':
        return (
          <TooltipHint
            tooltip={this._renderErrorTooltip(itemVariant)}
            glyph={this._renderMissingAttributesInfo(itemVariant)}
            noWrapper
            placement="bottom"
          />
        );
      case 'PENDING_APPROVAL':
        return (
          <TooltipHint
            tooltip={this._renderErrorTooltip(itemVariant)}
            glyph={this._renderPendingInfo()}
            noWrapper
            placement="bottom"
          />
        );
      default:
        return null;
    }
  };

  _renderMissingAttributesInfo = itemVariant => {
    const { fields } = itemVariant.error_details;
    return (
      <div>
        <div
          className={classNames(
            styles['font-color-thin'],
            styles['missing-attributes-desc']
          )}
        >
          {`${fields.length} missing`}
        </div>
        <i
          className={classNames(
            'glyphicon glyphicon-alert',
            styles['warning-icon'],
            styles['ml-10']
          )}
        />
      </div>
    );
  };

  _renderPendingInfo = () => {
    return (
      <div>
        <div
          className={classNames(
            styles['font-color-thin'],
            styles['missing-attributes-desc']
          )}
        >
          Info
        </div>
        <i
          className={classNames(
            'glyphicon glyphicon-question-sign',
            styles['info-icon'],
            styles['ml-10']
          )}
        />
      </div>
    );
  };

  _renderErrorTooltip = itemVariant => {
    switch (itemVariant.error_type) {
      case 'MISSING_ATTRIBUTES':
        let s = itemVariant.error_details.fields.reduce(
          (prev, curr) => `${prev + curr}, `,
          ''
        );
        s = s.substr(0, s.length - 2);

        return (
          <span
            className={classNames(
              styles['tooltip-text'],
              styles['text-align-left']
            )}
          >
            <span className={styles['font-color-thin']}>Missing</span>: {s}.
            <br />
            <br />
            Complete the missing attributes in{' '}
            <a href="/library/list" target="_blank">
              item library
            </a>{' '}
            page when you add or edit an item.
          </span>
        );
      case 'PENDING_APPROVAL':
        return (
          <span
            className={classNames(
              styles['tooltip-text'],
              styles['text-align-left']
            )}
          >
            <span className={styles['font-color-thin']}>Pending Approval</span>
            <br />
            <br />
            {itemVariant.error_details.message}.
          </span>
        );
    }
  };

  _renderStatus = (isUploading, itemVariant) => {
    if (isUploading) {
      return (
        <div>
          <img
            src={loadingIcon}
            className={classNames(styles['img-loading'])}
          />
          <span
            className={classNames(styles['ml-7'], styles['font-color-thin'])}
          >
            Uploading
          </span>
        </div>
      );
    }

    const { status } = itemVariant;

    return (
      <div>
        <span
          className={classNames(
            styles['status-indicator'],
            styles[status.toLowerCase()]
          )}
        />
        <span
          className={classNames(
            styles['ml-10'],
            styles[`status-${status.toLowerCase()}`]
          )}
        >
          {status}
        </span>
      </div>
    );
  };

  _onReachBottom = () => {
    const {
      shopDetail,
      shopDetailProducts,
      shopDetailProductsRequest: productRequest,
    } = this.props.shop;

    this.props.dispatch(
      shopDetailProductsRequest(
        shopDetail.id,
        shopDetailProducts.page + 1,
        productRequest.status
      )
    );
  };

  _hasMore = () => {
    const { completed } = this.props.shop.shopDetailProducts;
    return !completed;
  };
}

function mapStateToProps(state) {
  return {
    shop: state.onlineStore.shop,
  };
}

export default connect(mapStateToProps)(ShopDetailProductTable);
